
  import { defineComponent, ref } from 'vue';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useI18n } from 'vue-i18n';
  import CustomDatePicker from '@/components/search/CustomDatePicker.vue';

  export default defineComponent({
    name: 'ads-breakdown-widget',
    props: {
      widgetClasses: String,
      entityType: {
        type: String,
        default: 'ads',
      },
    },
    components: {
      CustomDatePicker,
    },

    async setup() {
      const store = useStore();
      const loadingPublished = ref(false);
      const selectedPeriod = ref('last-7-days');
      const { t, te } = useI18n();

      const translate = (text) => (te(text) ? t(text) : text);

      const customDateFrom = ref('');
      const customDateTo = ref('');
      const showDatePicker = ref(false);

      const transactionTypes = [
        { key: 'sale', label: 'Sale' },
        { key: 'rent', label: 'Rent' },
        { key: 'buy', label: 'Wanted Sale' },
        { key: 'torent', label: 'Wanted Rent' },
        { key: 'exchange', label: 'Exchange' },
      ];

      const enteredAdsSection = ref({
        regular: { sale: 0, rent: 0, buy: 0, torent: 0, exchange: 0 },
        special: { sale: 0, rent: 0, buy: 0, torent: 0, exchange: 0 },
      });

      const activeAdsSection = ref({
        regular: { sale: 0, rent: 0, buy: 0, torent: 0, exchange: 0 },
        special: { sale: 0, rent: 0, buy: 0, torent: 0, exchange: 0 },
      });

      const showCustomDatePicker = () => {
        showDatePicker.value = true;
        selectedPeriod.value = 'custom';
      };

      const applyCustomDates = async () => {
        if (!customDateFrom.value || !customDateTo.value) {
          alert('Please select both dates');
          return;
        }
        showDatePicker.value = false;
        await selectPeriod('custom');
      };

      const fetchActiveAds = async () => {
        await store.dispatch(Actions.GET_ACTIVE_ADS_TYPES_SUMMARY_DASHBOARD);
        const response = store.getters.activeAdsTypesSummaryDashboard;

        if (response?.data) {
          activeAdsSection.value.regular = {
            sale: response.data.regular?.sale || 0,
            rent: response.data.regular?.rent || 0,
            buy: response.data.regular?.buy || 0,
            torent: response.data.regular?.torent || 0,
            exchange: response.data.regular?.exchange || 0,
          };
          activeAdsSection.value.special = {
            sale: response.data.special?.sale || 0,
            rent: response.data.special?.rent || 0,
            buy: response.data.special?.buy || 0,
            torent: response.data.special?.torent || 0,
            exchange: response.data.special?.exchange || 0,
          };
        }
      };

      const fetchData = async (period: string) => {
        loadingPublished.value = true;
        try {
          const obj = {
            dateRange: period,
            showTrend: false,
            dateFrom: '',
            dateTo: '',
          };

          if (
            period === 'custom' &&
            customDateFrom.value &&
            customDateTo.value
          ) {
            obj.dateFrom = customDateFrom.value;
            obj.dateTo = customDateTo.value;
          }

          await store.dispatch(Actions.GET_ADS_TYPES_SUMMARY_DASHBOARD, obj);
          const response = store.getters.adsTypesSummaryDashboard;

          if (response?.data) {
            enteredAdsSection.value.regular = {
              sale: response.data.regular?.sale || 0,
              rent: response.data.regular?.rent || 0,
              buy: response.data.regular?.buy || 0,
              torent: response.data.regular?.torent || 0,
              exchange: response.data.regular?.exchange || 0,
            };
            enteredAdsSection.value.special = {
              sale: response.data.special?.sale || 0,
              rent: response.data.special?.rent || 0,
              buy: response.data.special?.buy || 0,
              torent: response.data.special?.torent || 0,
              exchange: response.data.special?.exchange || 0,
            };
          }
        } catch (error) {
          console.error('Error fetching ads types summary:', error);
        } finally {
          loadingPublished.value = false;
        }
      };

      const selectPeriod = async (period: string) => {
        selectedPeriod.value = period;
        await fetchData(period);
      };

      try {
        await Promise.all([fetchData('last-7-days'), fetchActiveAds()]);
      } catch (error) {
        console.error(
          'Error during initial data load in AdsTypesSummaryWidget:',
          error
        );
      }

      return {
        translate,
        loadingPublished,
        selectedPeriod,
        transactionTypes,
        enteredAdsSection,
        activeAdsSection,
        selectPeriod,
        showCustomDatePicker,
        customDateFrom,
        customDateTo,
        showDatePicker,
        applyCustomDates,
      };
    },
  });
